<template>
  <div>
    <v-dialog
      v-model="isVisible"
      :fullscreen="isFullScreen"
      max-width="850"
      persistent
      scrollable
    >
      <v-card>
        <v-toolbar
          class="pl-1"
          flat
          height="80"
          max-height="80"
        >
          <div class="mr-3">
            <v-avatar color="blue">
              <v-icon color="blue lighten-5">
                {{ $t('Marketing.Sms.Dialog.Templates.Toolbar.Icon') }}
              </v-icon>
            </v-avatar>
          </div>

          <v-toolbar-title class="pl-1">
            <div class="body-3">
              {{ $t('Marketing.Sms.Dialog.Templates.Toolbar.Title') }}
            </div>

            <div class="text-caption">
              {{ $t('Marketing.Sms.Dialog.Templates.Toolbar.SubTitle') }}
            </div>
          </v-toolbar-title>

          <v-spacer />

          <v-btn
            class="ml-0"
            icon
            @click="onCancelButtonClick"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-progress-linear
          v-if="isLoading"
          height="2"
          indeterminate
        />

        <v-divider />

        <v-card-text class="pa-3">
          <v-container
            :class="[{'fill-height': !templatesCount}]"
            class="pa-0"
            fluid
          >
            <v-row v-if="viewData">
              <template v-for="template in templates">
                <v-col
                  :key="`template-${template.Id}`"
                  cols="auto"
                >
                  <v-lazy
                    :options="{threshold: .5}"
                    transition="fade-transition"
                  >
                    <v-hover v-slot="{hover}">
                      <v-card
                        max-width="357"
                        width="357"
                      >
                        <v-card
                          class="text-center rounded-b-0 indigo overflow-hidden"
                          flat
                          height="160"
                        >
                          <div class="d-flex fill-height align-center justify-center overflow-hidden">
                            <v-sheet
                              color="white"
                              flat
                              light
                              min-height="120"
                              rounded
                              width="90%"
                            >
                              <v-card-text class="text-start">
                                {{ (template.Body || 'Message...') | truncate(160) }}
                              </v-card-text>
                            </v-sheet>
                          </div>

                          <v-expand-transition>
                            <div
                              v-if="hover"
                              class="d-flex transition-fast-in-fast-out white--text v-card--reveal"
                            >
                              <div class="text-center">
                                <v-btn
                                  v-if="mode === 'edit'"
                                  :color="$t('Marketing.Sms.Dialog.Templates.Button.New.Color')"
                                  :disabled="template.loadingEdit"
                                  :loading="template.loadingEdit"
                                  class="my-2"
                                  dark
                                  depressed
                                  style="min-width: 255px"
                                  @click.stop="onTemplateNewClick(template)"
                                >
                                  <v-icon left>
                                    {{ $t('Marketing.Sms.Dialog.Templates.Button.New.Icon') }}
                                  </v-icon>

                                  <div>{{ $t('Marketing.Sms.Dialog.Templates.Button.New.Title') }}</div>
                                </v-btn>

                                <v-btn
                                  v-if="template.Id > 0 && mode === 'edit'"
                                  :color="$t('Marketing.Sms.Dialog.Templates.Button.Edit.Color')"
                                  :disabled="template.loadingEdit"
                                  :loading="template.loadingEdit"
                                  class="my-2"
                                  dark
                                  depressed
                                  style="min-width: 255px"
                                  @click.stop="onTemplateEditClick(template)"
                                >
                                  <v-icon left>
                                    {{ $t('Marketing.Sms.Dialog.Templates.Button.Edit.Icon') }}
                                  </v-icon>

                                  <div>{{ $t('Marketing.Sms.Dialog.Templates.Button.Edit.Title') }}</div>
                                </v-btn>

                                <v-btn
                                  v-if="mode === 'select'"
                                  :color="$t('Marketing.Sms.Dialog.Templates.Button.Select.Color')"
                                  :disabled="template.loadingSelect"
                                  :loading="template.loadingSelect"
                                  class="my-2"
                                  dark
                                  depressed
                                  style="min-width: 255px"
                                  @click.stop="onTemplateSelectClick(template)"
                                >
                                  <v-icon left>
                                    {{ $t('Marketing.Sms.Dialog.Templates.Button.Select.Icon') }}
                                  </v-icon>

                                  <div>{{ $t('Marketing.Sms.Dialog.Templates.Button.Select.Title') }}</div>
                                </v-btn>

                                <v-btn
                                  v-if="template.Id > 0 && mode === 'edit'"
                                  :color="$t('Marketing.Sms.Dialog.Templates.Button.Delete.Color')"
                                  :disabled="template.loadingDelete"
                                  :loading="template.loadingDelete"
                                  class="my-2"
                                  depressed
                                  style="min-width: 255px"
                                  text
                                  @click.stop="onTemplateDeleteClick(template)"
                                >
                                  <v-icon left>
                                    {{ $t('Marketing.Sms.Dialog.Templates.Button.Delete.Icon') }}
                                  </v-icon>

                                  <div>{{ $t('Marketing.Sms.Dialog.Templates.Button.Delete.Title') }}</div>
                                </v-btn>
                              </div>
                            </div>
                          </v-expand-transition>
                        </v-card>

                        <v-divider />

                        <v-card-title>
                          <v-icon
                            class="mt-1"
                            color="indigo"
                            left
                          >
                            mdi-message-text-outline
                          </v-icon>

                          {{ (template.Title || 'Title...') | truncate(23) }}
                        </v-card-title>
                      </v-card>
                    </v-hover>
                  </v-lazy>
                </v-col>
              </template>
            </v-row>

            <v-row
              v-else-if="!isLoading"
              class="text-center justify-center"
            >
              <v-col cols="12">
                <v-data-table-no-data
                  :button-callback="onTemplateNewClick"
                  :no-data-obj="mode === 'edit' ? $t('Marketing.Sms.Dialog.Templates.NoDataEdit') : $t('Marketing.Sms.Dialog.Templates.NoDataSelect')"
                >
                  <template
                    v-if="appNotifySmsActive"
                    #cardActionsAppend
                  >
                    <v-card-actions class="px-4 pb-4 justify-center">
                      <v-btn
                        :color="$t('Marketing.Sms.Dialog.Templates.NoDataSelect.ButtonCampaign.Color')"
                        block
                        class="pr-3"
                        dark
                        depressed
                        large
                        @click="onTemplateSelectClick(null)"
                      >
                        <v-icon left>
                          {{ $t('Marketing.Sms.Dialog.Templates.NoDataSelect.ButtonCampaign.Icon') }}
                        </v-icon>

                        {{ $t('Marketing.Sms.Dialog.Templates.NoDataSelect.ButtonCampaign.Title') }}
                      </v-btn>
                    </v-card-actions>
                  </template>
                </v-data-table-no-data>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-3">
          <v-pagination
            v-if="!!templatesCount"
            v-model="page"
            :length="totalPages"
            :total-visible="$vuetify.breakpoint.xsOnly ? 0 : 7"
          />

          <v-spacer />

          <v-btn
            class="light-blue--text text--darken-1"
            outlined
            @click="onCancelButtonClick"
          >
            {{ $t('Common.Button.Close').toLocaleUpperCase($i18n.locale) }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <confirm-dialog
      v-if="selectedTemplateDelete"
      :html-content="$t('Message.Template.ConfirmDeleteDialog.Content', {title: selectedTemplateDelete.Title})"
      :html-title="$t('Message.Template.ConfirmDeleteDialog.Title')"
      :info-bar="true"
      :info-bar-body="$t('Message.Template.ConfirmDeleteDialog.Warning')"
      :visible.sync="confirmDialogDeleteVisible"
      :width="560"
      no-button-class="v-btn--outline green--text"
      title-class="red white--text lighten-0"
      yes-button-class="red white--text"
      yes-button-event="on-confirm-delete"
      @on-confirm-delete="onConfirmDeleteTemplate"
    />

    <sms-template-edit-dialog
      v-if="templateEditDialogVisible"
      :template="selectedTemplateEdit"
      :visible.sync="templateEditDialogVisible"
    />
  </div>
</template>

<script>
import Auth from '@/mixins/auth.js'
import AppData from '@/mixins/appdata.js'
import DialogVisible from '@/mixins/dialogVisible.js'
import ConfirmDialog from '@/components/common/ConfirmDialog.vue'
import VDataTableNoData from '@/components/common/data-table/VDataTableNoData.vue'
import SmsTemplateEditDialog from '@/components/marketing/sms/SmsTemplateEditDialog.vue'
import {clone} from '@/lib/helper/helper.js'

export default {
  name      : 'SmsTemplatesDialog',
  components: {
    SmsTemplateEditDialog,
    VDataTableNoData,
    ConfirmDialog
  },
  mixins: [Auth, AppData, DialogVisible],
  props : {
    mode: {
      type   : String,
      default: 'edit',
      validator(value) {
        return ['edit', 'select'].includes(value)
      }
    }
  },
  data() {
    return {
      isFullScreen: true,
      isLoading   : false,
      viewData    : null,
      page        : 1,
      perPage     : 14,

      selectedTemplateDelete: null,
      selectedTemplateEdit  : null,

      confirmDialogDeleteVisible: false,
      templateEditDialogVisible : false
    }
  },
  computed: {
    templates() {
      const retVal = this.viewData?.items || []
      return [...retVal]
    },

    templatesCount() {
      return this.viewData?.count || 0
    },

    totalPages() {
      return Math.ceil(this.templatesCount / this.perPage)
    }
  },
  watch: {
    page() {
      this.getData()
    },

    isVisible(newVal) {
      if (newVal) {
        this.getData()
      } else {
        this.viewData = null
      }
    }
  },
  created() {
    this.$bus.$on(window.SocketCommand.Marketing.Sms.Template.All, this.onGetDataResult)
    this.$bus.$on(window.SocketCommand.Marketing.Sms.Template.Save, this.onTemplateSaveResult)
    this.$bus.$on(window.SocketCommand.Marketing.Sms.Template.Delete, this.onTemplateDeleteResult)
  },
  mounted() {
  },
  updated() {
  },
  beforeDestroy() {
    this.$bus.$off(window.SocketCommand.Marketing.Sms.Template.All, this.onGetDataResult)
    this.$bus.$off(window.SocketCommand.Marketing.Sms.Template.Save, this.onTemplateSaveResult)
    this.$bus.$off(window.SocketCommand.Marketing.Sms.Template.Delete, this.onTemplateDeleteResult)
  },
  methods: {
    getData() {
      this.isLoading = true
      window.callAS(window.SocketCommand.Marketing.Sms.Template.All, {
        page      : this.page,
        page_limit: this.perPage
      })
    },

    onGetDataResult(data) {
      this.viewData = data?.count ? data : null
      this.isLoading = false
      if (Array.isArray(this.viewData?.items)) {
        this.viewData.items.unshift(
          {
            Id   : -1,
            Title: this.$t('Marketing.Sms.Dialog.Templates.Blank.Title'),
            Body : this.$t('Marketing.Sms.Dialog.Templates.Blank.Subject')
          }
        )
      }
    },

    onTemplateSelectClick(template) {
      if (template?.Id > 0) {
        this.$emit('template:selected', template)
      } else {
        this.$emit('template:selected', null)
      }

      this.onCancelButtonClick()
    },

    onTemplateNewClick(template) {
      const payload = clone(template)
      payload.Id = null
      this.showEditTemplateDialog(payload)
    },

    onTemplateEditClick(template) {
      if (template?.Id) {
        this.showEditTemplateDialog(template)
      } else {
        this.showEditTemplateDialog()
      }
    },

    showEditTemplateDialog(template) {
      this.selectedTemplateEdit = template || null
      this.templateEditDialogVisible = true
    },

    onTemplateDeleteClick(template) {
      this.selectedTemplateDelete = template
      this.confirmDialogDeleteVisible = true
    },
    onConfirmDeleteTemplate() {
      this.confirmDialogDeleteVisible = false
      this.$set(this.selectedTemplateDelete, 'loadingDelete', true)
      window.callAS(window.SocketCommand.Marketing.Sms.Template.Delete, {Id: this.selectedTemplateDelete.Id})
    },
    onTemplateDeleteResult(data, payload) {
      if (data?.status === 'success') {
        this.getData()

        if (data?.status === 'success') {
          if (data && data.status === 'success') {
            this.$bus.$emit('app-show-notification', {
              body: this.$t('Message.Template.DeleteSuccess'),
              type: 'success',
              icon: 'info'
            })
          } else {
            this.$bus.$emit('app-show-notification', {
              body: this.$t('Common.Error.Generic'),
              type: 'error',
              icon: 'warning'
            })
          }
        }
      }
    },

    onTemplateSaveResult(data, payload) {
      if (data?.status === 'success') {
        this.getData()
      }
    },

    onCancelButtonClick() {
      this.closeDialog()
    }
  }
}
</script>

<style scoped>
:deep(.v-card--reveal .v-btn__content) {
  justify-content: flex-start;
}

:deep(.v-card--reveal .v-btn__content div) {
  text-align: center;
  width: 100%;
}

.v-card--reveal {
  position: absolute;
  align-items: center;
  justify-content: center;
  bottom: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(30, 30, 30, 0.90);
}
</style>
